import { useEffect, useState } from 'react'
import makeStyles from '@mui/styles/makeStyles'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TableSortLabel from '@mui/material/TableSortLabel'
import { useSelector } from '../../redux/hooks/useSelector'
import { CANCLE_ORDERS_DIALOG_ID } from '../../constants/dialogConstants'
import { OrderEditDialog } from '../dialogs/OrderEditDialog'
import { OrderDetailDialog } from '../dialogs/OrderDetailDialog'
import { useOrderFilters } from '../../hooks/useOrderFilters'
import { useTranslation } from 'react-i18next'
import { ORDER_STATE_CONFIRMED, ORDER_STATE_NEW, ORDER_STATE_PARTIAL_FILLED } from '../../constants/codeLists'
import { CancleOrdersDialog } from '../dialogs/CancleOrdersDialog'
import classNames from 'classnames'
import OrderTableRow from './OrderTableRow'
import OrderMobileCard from './OrderMobileCard'
import { newThemeObject } from '../../newTheme'
import { tokens } from '../../styles/tokens'
import CardWrapper from '../common/CardWrapper'
import { isApexUser } from '../../utils/apex'
import useMobileWidth from '../../hooks/useMobileWidth'

const useStyles = makeStyles(theme => ({
  sectionHeader: {
    background: `linear-gradient(135deg, ${tokens.color.primary[800]}, ${tokens.color.primary[600]})`,
    margin: '-16px -16px 16px',
    padding: '12px 16px',
    borderRadius: `${tokens.radius.lg} ${tokens.radius.lg} 0 0`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    minHeight: '48px',
  },
  sectionHeaderTitle: {
    color: tokens.color.white,
    fontSize: '0.9375rem',
    fontWeight: 600,
    letterSpacing: '-0.01em',
    margin: 0,
    display: 'flex',
    alignItems: 'center',
  },
  tableHead: {
    ...newThemeObject.table.tableHead,
  },
  tableHeadChildrenText: {
    ...newThemeObject.table.tableHeadChildrenText,
  },
  tableHeadChildren: {
    ...newThemeObject.table.tableHeadChildren,
  },
  tableHeadChildrenFirst: {
    ...newThemeObject.table.tableHeadChildrenFirst,
  },
  tableHeadChildrenLast: {
    ...newThemeObject.table.tableHeadChildrenLast,
  },
  table: {
    minWidth: 650,
    [theme.breakpoints.down('sm')]: {
      minWidth: 'auto',
    },
  },
  iconSize: {
    height: '20px',
    width: '40px',
    lineHeight: '18px',
    fontSize: '10px',
  },
  columnCell: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'start',
    color: '#000',
  },
  // Karty pokynů pro mobil - nahrazuji sirokou tabulku, aby se nemuselo nic posouvat do stran.
  mobileList: {
    display: 'flex',
    flexDirection: 'column',
  },
}))

export interface OrderRowData {
  surname: string
  firstName: string
  companyName: string
  amount: number
  instrumentType: string
  security: string
  currency: string
  state: string
  sendToFix: boolean
  clientId: number
  securityId: number
  orderId: number
  direction: string
  orderIdent: string
  orderCreationDate: Date
  validityType: string
  validityDate: string
  price: number
  quantity: number
  isin: string
  symbol: string
  ticker: string
  orderMarketType: string
  assetClass: string
}

function createData(
  surname: string,
  firstName: string,
  companyName: string,
  amount: number,
  instrumentType: string,
  security: string,
  currency: string,
  state: string,
  sendToFix: boolean,
  clientId: number,
  securityId: number,
  orderId: number,
  direction: string,
  orderIdent: string,
  orderCreationDate: Date,
  validityType: string,
  validityDate: string,
  price: number,
  quantity: number,
  isin: string,
  symbol: string,
  ticker: string,
  orderMarketType: string,
  assetClass: string,
) {
  return {
    surname,
    firstName,
    companyName,
    amount,
    instrumentType,
    security,
    currency,
    state,
    sendToFix,
    clientId,
    securityId,
    orderId,
    direction,
    orderIdent,
    orderCreationDate,
    validityType,
    validityDate,
    price,
    quantity,
    isin,
    symbol,
    ticker,
    orderMarketType,
    assetClass,
  }
}

type SortField = 'orderCreationDate' | 'client' | 'direction' | 'validity' | 'price' | 'amount' | 'security' | 'state' | 'sendToFix'

// Radi hodnoty ruznych typu (retezce, cisla, datumy, boolean) pro razeni sloupcu tabulky pokynu.
const compareValues = (a: any, b: any) => {
  if (a instanceof Date && b instanceof Date) {
    const at = a.getTime()
    const bt = b.getTime()
    if (isNaN(at) && isNaN(bt)) return 0
    if (isNaN(at)) return -1
    if (isNaN(bt)) return 1
    return at - bt
  }
  if (typeof a === 'boolean' && typeof b === 'boolean') return Number(a) - Number(b)
  if (typeof a === 'string' && typeof b === 'string') return a.localeCompare(b)
  return a > b ? 1 : a < b ? -1 : 0
}

const getSortValue = (row: OrderRowData, field: SortField) => {
  switch (field) {
    case 'client':
      return row.companyName || `${row.surname}${row.firstName}`
    case 'validity':
      return new Date(row.validityDate)
    case 'security':
      return row.security
    default:
      return row[field]
  }
}

interface OrderTableProps {
  setOrderCheck: (orderId: number, valueDiary: boolean) => any
}

export const OrderTable = ({setOrderCheck}: OrderTableProps) => {
  const classes = useStyles()
  const orderTableRows = useOrderFilters()
  const user = useSelector(state => state.auth.user)
  const { t } = useTranslation()
  const isMobile = useMobileWidth()
  const [sortField, setSortField] = useState<SortField | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  const rows = orderTableRows.map(row =>
    createData(
      row.clientSurname,
      row.clientFirstname,
      row.companyName,
      row.amount,
      row.instrumentType,
      row.securityName,
      row.currency,
      row.state,
      row.sendToFix,
      row.clientId,
      row.securityId,
      row.orderId,
      row.direction,
      row.orderIdent,
      row.orderCreationDate,
      row.validityType,
      row.validityDate,
      row.price,
      row.quantity,
      row.isin,
      row.symbol,
      row.ticker,
      row.orderMarketType,
      row.assetClass
    )
  )

  if (sortField) {
    rows.sort((a, b) => {
      const result = compareValues(getSortValue(a, sortField), getSortValue(b, sortField))
      return sortDirection === 'asc' ? result : -result
    })
  }

  const isEditDisabled = (state: string) => {
    // Editable and cancelable states are new, confirmed and partially filled
    return state !== ORDER_STATE_NEW && state !== ORDER_STATE_CONFIRMED && state !== ORDER_STATE_PARTIAL_FILLED
  }

  return (
    <>
      <TableContainer component={CardWrapper}>
        <div className={classes.sectionHeader}>
          <p className={classes.sectionHeaderTitle}>{isApexUser(user) ? t('EVIDENCE_ASSIGNED_UNITS') : t('EVIDENCE_SENT_ORDERS')}</p>
        </div>
        {isMobile ? (
          <div className={classes.mobileList}>
            {rows.map((row, index) => (
              <OrderMobileCard key={index} row={row} isLast={index === rows.length - 1} setOrderCheck={setOrderCheck} />
            ))}
          </div>
        ) : (
        <Table className={`${classes.table}`} size="small" aria-label="simple table">
          <TableHead>
            <TableRow className={classes.tableHead}>
              <TableCell className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenFirst])} />
              <TableCell className={classes.tableHeadChildren}>
                <p className={classes.tableHeadChildrenText}>{t('ORDER_NUMBER')}</p>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'orderCreationDate'}
                  direction={sortField === 'orderCreationDate' ? sortDirection : 'asc'}
                  onClick={() => handleSort('orderCreationDate')}
                >
                  {t('CRETATION_DATE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'client'}
                  direction={sortField === 'client' ? sortDirection : 'asc'}
                  onClick={() => handleSort('client')}
                >
                  {t('CLIENT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'direction'}
                  direction={sortField === 'direction' ? sortDirection : 'asc'}
                  onClick={() => handleSort('direction')}
                >
                  {t('DIRECTION_ORDER_TYPE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'validity'}
                  direction={sortField === 'validity' ? sortDirection : 'asc'}
                  onClick={() => handleSort('validity')}
                >
                  {t('DATE_FROM')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'price'}
                  direction={sortField === 'price' ? sortDirection : 'asc'}
                  onClick={() => handleSort('price')}
                >
                  {t('PRICE_UNIT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'amount'}
                  direction={sortField === 'amount' ? sortDirection : 'asc'}
                  onClick={() => handleSort('amount')}
                >
                  {t('AMOUNT_CURRENCY')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'security'}
                  direction={sortField === 'security' ? sortDirection : 'asc'}
                  onClick={() => handleSort('security')}
                >
                  {t('INSTRUMENT')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'state'}
                  direction={sortField === 'state' ? sortDirection : 'asc'}
                  onClick={() => handleSort('state')}
                >
                  {t('ORDER_STATE')}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHeadChildren}>
                <TableSortLabel
                  className={classes.tableHeadChildrenText}
                  active={sortField === 'sendToFix'}
                  direction={sortField === 'sendToFix' ? sortDirection : 'asc'}
                  onClick={() => handleSort('sendToFix')}
                >
                  {t('SEND_TO_FIX')}
                </TableSortLabel>
              </TableCell>
              <TableCell  className={classNames([classes.tableHeadChildren, classes.tableHeadChildrenLast])}/>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => (
              <OrderTableRow key={index} row={row} isLast={index === rows.length - 1} setOrderCheck={setOrderCheck}/>
            ))}
          </TableBody>
        </Table>
        )}
      </TableContainer>
      <OrderEditDialog />
      <OrderDetailDialog />
    </>
  )
}
